// Catalogue of CoSec Global service + feature pages.
// All routes are matched by slug, kind "service" → ServicePage, "feature" → FeaturePage.
// Images are reused from existing pages (Hero, Personas, Blog, Case Studies covers).
import {
  Stamp, FileText, FilePlus2, Globe2, Repeat2, FileSignature, BadgePercent,
  Pencil, BarChart3, Building2, Send, MapPin, UserCheck, Users, ShieldCheck,
  Briefcase, ScrollText, Calendar, Bell, Workflow, PenLine, BadgeCheck,
  ArrowUpRightSquare, KeyRound, MonitorSmartphone, GitBranch, Network,
} from "lucide-react";

// Reusable image bank (already on the site)
const IMG = {
  doc:      "https://images.unsplash.com/photo-1554224154-26032ffc0d07?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  desk:     "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  meeting:  "https://images.unsplash.com/photo-1573166364524-d9dbfd8bbf83?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  board:    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  laptop:   "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  legal:    "https://images.unsplash.com/photo-1505664194779-8beaceb93744?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  team:     "https://images.unsplash.com/photo-1573164713988-8665fc963095?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  pe:       "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  studio:   "https://images.unsplash.com/photo-1554224154-22dec7ec8818?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  mobile:   "https://images.unsplash.com/photo-1509017174183-0b7e0278f1ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

// ============================================================
//  SERVICES, paid-for-you Cosec & filing services
// ============================================================
export const SERVICES = [
  {
    slug: "company-formation",
    kind: "service",
    Icon: Building2,
    eyebrow: "Form a UK company",
    title: "Form a UK company",
    highlight: "in one working day.",
    subhead: "Fast, AI-verified incorporation with Companies House, registered office, statutory registers and ECCTA evidence all set up from day one.",
    intro: "Our Companies House Authorised gateway lets us file a new limited company quickly, then pre-populate your statutory registers, director and PSC records, and ECCTA-compliant identity verification inside CoSec App. You walk away with a live company number plus everything you need to operate, in one workflow.",
    image: IMG.desk,
    benefits: [
      { t: "Authorised filer", d: "Submitted via the Companies House software gateway." },
      { t: "ECCTA-ready", d: "Director ID verification, registered email, all captured." },
      { t: "Registers from day one", d: "Statutory registers pre-built inside CoSec App." },
      { t: "Companion services", d: "Optional registered office + apostilled documents." },
    ],
    includes: [
      "Companies House CS01 / IN01 submission",
      "Memorandum & articles drafting",
      "PSC + director ECCTA evidence",
      "Statutory registers initialised inside CoSec App",
      "First-year reminder schedule",
      "Companies House certificate (PDF)",
    ],
    howItWorks: [
      { t: "Tell us about the company", d: "Name, directors, PSCs, share structure. 5 minutes via our portal." },
      { t: "ECCTA identity verification", d: "Every director and PSC runs through CoVerify." },
      { t: "We file with Companies House", d: "Authorised gateway, typically same day." },
      { t: "Registers go live in CoSec App", d: "Day one, ready for first board minutes." },
    ],
    pricing: { from: "£89", plan: "Per incorporation" },
    faqs: [
      { q: "How long does company formation take?", a: "Most UK limited companies are incorporated within one working day. Same-day formation is available for an additional fee, subject to Companies House cut-off times." },
      { q: "What's included in the price?", a: "Companies House filing fee, drafting of memorandum & articles, initial statutory registers in CoSec App, and one director ID verification via CoVerify. Additional directors or PSCs are charged per verification." },
      { q: "Do I need a UK registered office to incorporate?", a: "Yes, every UK company must have a UK registered office. We offer this as a bundled service (see Registered Office Service)." },
    ],
    related: ["psc-services", "statutory-registers", "register-office-address-service"],
  },
  {
    slug: "confirmation-statement",
    kind: "service",
    Icon: FileText,
    eyebrow: "Annual confirmation statement",
    title: "File your CS01",
    highlight: "without breaking a sweat.",
    subhead: "We prepare and file your Companies House confirmation statement (CS01) from your live CoSec App data, every year, on time, with full audit trail.",
    intro: "Missing a CS01 deadline can lead to a Companies House strike-off. We monitor every entity's confirmation date, prepare the statement from your live statutory data inside CoSec App, send a one-tap approval to the company secretary, and file via the Companies House gateway with a signed receipt.",
    image: IMG.doc,
    benefits: [
      { t: "Deadline monitoring", d: "Automatic alerts 60, 30 and 7 days before due date." },
      { t: "Pre-populated from data", d: "PSC, share, director changes auto-included." },
      { t: "Bulk filing for portfolios", d: "Up to 500 entities per submission window." },
      { t: "Signed audit trail", d: "Receipt + audit log written to the entity." },
    ],
    includes: [
      "CS01 preparation from CoSec App data",
      "Pre-filing data integrity check",
      "Approval workflow (secretary + director)",
      "Filing via Companies House gateway",
      "Filing receipt + audit log",
      "Companies House fee included",
    ],
    howItWorks: [
      { t: "We monitor the deadline", d: "Auto-alerts trigger 60 days before due date." },
      { t: "Draft prepared", d: "From live CoSec App data, review in one click." },
      { t: "Approve & file", d: "One-tap approval from the secretary." },
      { t: "Receipt + audit log", d: "Filed receipt written back to the entity." },
    ],
    pricing: { from: "£50", plan: "Per filing" },
    faqs: [
      { q: "When is the confirmation statement due?", a: "A UK company's confirmation statement is due 14 days after its confirmation date (typically the anniversary of incorporation). Late filing can lead to civil and criminal penalties for the directors." },
      { q: "Can you handle multiple entities at once?", a: "Yes. CoSec App supports bulk preparation and filing for accountancy firms and PE portfolios, typically 50–500 entities per cycle." },
      { q: "Do you charge per filing or annually?", a: "Per filing, there are no annual licence fees on top." },
    ],
    related: ["statutory-registers", "psc-services", "officer-appointments-resignations"],
  },
  {
    slug: "psc-services",
    kind: "service",
    Icon: ShieldCheck,
    eyebrow: "Persons with Significant Control",
    title: "Manage every PSC",
    highlight: "to ECCT Act standard.",
    subhead: "Notifications, identity verification, register updates, handled end-to-end as new ECCT Act 2023 rules tighten.",
    intro: "Under the ECCT Act 2023, every PSC must be verified, evidenced and surfaced on the register with no delay. We handle the PSC lifecycle, new notifications, changes, cessations and identity verification, and write everything back to CoSec App so your statutory register is always in sync with Companies House.",
    image: IMG.legal,
    benefits: [
      { t: "ECCTA evidence", d: "Verification stored against every PSC." },
      { t: "Real-time PSC register", d: "Updated inside CoSec App on every event." },
      { t: "Companies House sync", d: "PSC notifications filed automatically." },
      { t: "Sanctions screening", d: "Daily PEP &amp; sanctions checks." },
    ],
    includes: [
      "PSC0X notifications (new, change, cease)",
      "Identity verification via CoVerify",
      "PSC register maintained in CoSec App",
      "PEP & sanctions screening (daily refresh)",
      "Audit trail per PSC event",
    ],
    howItWorks: [
      { t: "PSC event detected", d: "From a share transfer, change or new director." },
      { t: "Verification + screening", d: "ID check + AML screening through CoVerify." },
      { t: "PSC filing", d: "Notification to Companies House via gateway." },
      { t: "Register updated", d: "PSC register inside CoSec App + audit trail." },
    ],
    pricing: { from: "£18", plan: "Per PSC event" },
    faqs: [
      { q: "What counts as a PSC?", a: "Any person who holds more than 25% of shares or voting rights, or who has the right to appoint or remove a majority of directors, or who exercises significant influence or control. The full definition is in Schedule 1A of the Companies Act 2006." },
      { q: "How quickly must PSC changes be notified?", a: "PSC changes must be entered on the company's PSC register within 14 days of the company becoming aware, and notified to Companies House within a further 14 days." },
      { q: "Do you support relevant legal entities (RLEs)?", a: "Yes, registrable RLEs and other registrable persons are fully supported." },
    ],
    related: ["statutory-registers", "officer-appointments-resignations", "shareholder-management"],
  },
  {
    slug: "register-office-address-service",
    kind: "service",
    Icon: MapPin,
    eyebrow: "Registered office",
    title: "A London registered office -",
    highlight: "without the rent.",
    subhead: "Use our central London registered office address, with mail scanning, forwarding and on-demand mail handling.",
    intro: "Every UK company needs a registered office that's capable of receiving statutory mail. Use ours in central London (Wenlock Road) and we'll scan post to your CoSec App inbox, forward originals as needed, and handle HMRC and Companies House correspondence on your behalf.",
    image: IMG.desk,
    benefits: [
      { t: "Prestige address", d: "Central London N1." },
      { t: "Same-day mail scan", d: "Statutory post hits your inbox before lunch." },
      { t: "Forwarding included", d: "Originals forwarded weekly (next-day available)." },
      { t: "ECCTA-validated", d: "Capable of receiving mail, full compliance evidence." },
    ],
    includes: [
      "Use of registered office address",
      "Statutory mail scanned + emailed",
      "Original mail forwarded weekly",
      "HMRC & Companies House correspondence handled",
      "ECCTA registered-office evidence",
    ],
    howItWorks: [
      { t: "Choose your address", d: "London N1." },
      { t: "We update Companies House", d: "AD01 filed within 24 hours." },
      { t: "Mail starts flowing", d: "Same-day scan to your CoSec App inbox." },
    ],
    pricing: { from: "£12 / mo", plan: "Per company" },
    faqs: [
      { q: "Is your address ECCT Act 2023 compliant?", a: "Yes. Both locations are physical offices capable of receiving mail, with documented chain of custody for ECCTA registered-office validation." },
      { q: "Can I use this as my service address too?", a: "Yes, we offer a combined Registered Office + Service Address bundle." },
      { q: "How fast is mail forwarding?", a: "Statutory post is scanned same-day. Original forwarding is weekly by default; next-day forwarding is an optional upgrade." },
    ],
    related: ["service-address", "company-formation", "officer-appointments-resignations"],
  },
  {
    slug: "service-address",
    kind: "service",
    Icon: UserCheck,
    eyebrow: "Director / PSC service address",
    title: "Keep your home address",
    highlight: "off the public record.",
    subhead: "A service address for directors, secretaries and PSCs, fully ECCTA compliant, with mail scanning.",
    intro: "Every UK director and PSC has to record a service address on the public register. If you'd rather not publish your home address, use ours. Same setup as our registered office service, central London, with same-day mail scanning and weekly forwarding.",
    image: IMG.desk,
    benefits: [
      { t: "Privacy on the register", d: "Your home stays private." },
      { t: "Per-officer pricing", d: "Add as many directors as you need." },
      { t: "Companies House update", d: "We file CH01 / PSC07 forms for you." },
      { t: "Mail scanning included", d: "Statutory post emailed same-day." },
    ],
    includes: ["Service address for officers", "Same-day mail scan", "Companies House change of address filing", "Weekly forwarding of originals"],
    howItWorks: [
      { t: "Add the officers", d: "Director, secretary, PSC, any number." },
      { t: "Change-of-address filed", d: "CH01 / PSC07, typically same day." },
      { t: "Mail flowing", d: "Statutory post scanned to your inbox." },
    ],
    pricing: { from: "£8 / mo", plan: "Per officer" },
    faqs: [
      { q: "Can I add or remove officers later?", a: "Yes, add officers any time. We file the address change with Companies House the same day." },
      { q: "Is bundle pricing available?", a: "Yes, a Registered Office + Service Address combo gives a meaningful discount." },
    ],
    related: ["register-office-address-service", "officer-appointments-resignations"],
  },
  {
    slug: "officer-appointments-resignations",
    kind: "service",
    Icon: Users,
    eyebrow: "Officer appointments & resignations",
    title: "Add or remove an officer",
    highlight: "in minutes, not weeks.",
    subhead: "AP01, AP03, TM01, TM02 filings with full ECCT Act identity verification, handled end-to-end.",
    intro: "Appointing a director or secretary, or recording a resignation, looks simple, until you remember ECCT Act identity verification, statutory consent, register updates and the 14-day notification window. We do all four, with a signed audit trail.",
    image: IMG.team,
    benefits: [
      { t: "Same-day filing", d: "AP01 / AP03 / TM01 / TM02 in one workflow." },
      { t: "ECCTA verification", d: "New directors run through CoVerify." },
      { t: "Statutory consent captured", d: "Stored against the register entry." },
      { t: "Register auto-updated", d: "Inside CoSec App with audit trail." },
    ],
    includes: ["AP01 / AP03 / TM01 / TM02 forms filed", "ECCTA identity verification", "Statutory consent (signed)", "Register of Directors / Secretaries updated"],
    howItWorks: [
      { t: "Add or remove the officer", d: "One form in CoSec App." },
      { t: "Verify (if appointing)", d: "CoVerify handles ECCTA evidence." },
      { t: "We file with Companies House", d: "Within 24 hours." },
    ],
    pricing: { from: "£22", plan: "Per officer event" },
    faqs: [
      { q: "What about director ID verification under ECCTA?", a: "Every new director appointment runs through CoVerify by default. Verification status is stored against the register entry and surfaces on the company dashboard." },
      { q: "Can I bulk add officers across a group?", a: "Yes, useful for PE portfolio onboarding. Up to 50 officers per batch with shared verification settings." },
    ],
    related: ["psc-services", "statutory-registers", "people-management"],
  },
  {
    slug: "allot-shares",
    kind: "service",
    Icon: BarChart3,
    eyebrow: "Allotment of new shares",
    title: "Issue new shares -",
    highlight: "filing, registers, certificates.",
    subhead: "Resolutions, SH01 filing, register of members and printed share certificates, handled in one workflow.",
    intro: "Allotting new shares means more than a SH01. You need board resolutions, member approvals, updated PSC analysis, register of members updates and share certificates. We do all of it, with a single approval flow.",
    image: IMG.studio,
    benefits: [
      { t: "Resolutions drafted", d: "Board + shareholder, ready to e-sign." },
      { t: "SH01 filed", d: "Companies House gateway, same-day." },
      { t: "Certificates issued", d: "Printed + digital share certificates." },
      { t: "PSC re-analysis", d: "We flag any new PSCs after allotment." },
    ],
    includes: ["Board + shareholder resolutions", "SH01 filing", "Register of members updated", "Printed + digital share certificates", "PSC re-analysis after allotment"],
    howItWorks: [
      { t: "Tell us the structure", d: "Class, number, price, subscribers." },
      { t: "Resolutions + signatures", d: "e-Signed inside CoSec App." },
      { t: "SH01 filing + certificates", d: "Filed, issued, register updated." },
    ],
    pricing: { from: "£45", plan: "Per allotment" },
    faqs: [
      { q: "What does a share allotment usually involve?", a: "Board resolution to allot, shareholder approval (if needed), SH01 filing within one month, register of members update, share certificate issued within two months, and PSC re-analysis." },
      { q: "Do you handle preference shares and growth shares?", a: "Yes, preference shares, ordinary, growth, alphabet and EMI option pools are all supported." },
    ],
    related: ["share-capital", "shareholder-management", "issue-shares-certificate"],
  },
  {
    slug: "transfer-shares",
    kind: "service",
    Icon: Repeat2,
    eyebrow: "Share transfers",
    title: "Share transfers with",
    highlight: "stock transfer + stamp duty.",
    subhead: "Stock transfer forms, stamp duty, board resolutions and register updates, handled in one workflow.",
    intro: "A share transfer needs a stock transfer form, stamp duty (over £1,000), board approval, register of members update, possibly PSC re-analysis and a new share certificate. We coordinate the whole flow.",
    image: IMG.studio,
    benefits: [
      { t: "Stock transfer form drafted", d: "Pre-populated and e-signed." },
      { t: "Stamp duty handled", d: "HMRC J30 submitted on transfers >£1,000." },
      { t: "Register updated", d: "Members register auto-refreshed." },
      { t: "PSC re-analysis", d: "Triggered if ownership crosses thresholds." },
    ],
    includes: ["Stock transfer form (J30)", "Stamp duty submission (HMRC)", "Board approval workflow", "Register of members updated", "New share certificate issued"],
    howItWorks: [
      { t: "Capture transferor & transferee", d: "From your CoSec App register." },
      { t: "Stock transfer + stamp duty", d: "We file with HMRC." },
      { t: "Register + certificate", d: "Updated, issued, audit-trailed." },
    ],
    pricing: { from: "£38", plan: "Per transfer" },
    faqs: [
      { q: "When is stamp duty payable?", a: "On share transfers where the consideration exceeds £1,000. We submit the J30 to HMRC on your behalf." },
      { q: "Do you handle partial transfers?", a: "Yes, including transfers between joint holders and trustee transfers." },
    ],
    related: ["allot-shares", "shareholder-management", "issue-shares-certificate"],
  },
  {
    slug: "issue-shares-certificate",
    kind: "service",
    Icon: FileSignature,
    eyebrow: "Share certificates",
    title: "Printed share certificates,",
    highlight: "delivered to your door.",
    subhead: "Foil-stamped, numbered, secretary-signed certificates posted UK-wide. Digital copies also stored in CoSec App.",
    intro: "Statutory practice still expects a physical share certificate within two months of allotment or transfer. We print them on watermarked stock, foil-stamp the company seal, e-sign on behalf of the secretary, and post or courier to the shareholder.",
    image: IMG.studio,
    benefits: [
      { t: "Foil-stamped & numbered", d: "Watermarked compliance stock." },
      { t: "UK delivery", d: "Tracked next-day for time-critical certs." },
      { t: "Digital copy in CoSec App", d: "Linked to the register entry." },
      { t: "Re-issue support", d: "Lost certificates re-issued via deed of indemnity." },
    ],
    includes: ["Printed share certificate", "Digital copy in CoSec App", "Companies seal applied (digital)", "Tracked UK delivery"],
    howItWorks: [
      { t: "Trigger from allotment or transfer", d: "One click in CoSec App." },
      { t: "Printed + posted", d: "Next-business-day delivery." },
      { t: "Digital archive", d: "Stored against the member register." },
    ],
    pricing: { from: "£12", plan: "Per certificate" },
    faqs: [
      { q: "How long does delivery take?", a: "Standard delivery is 3–5 working days; next-day delivery is available as an upgrade." },
      { q: "Can certificates be re-issued if lost?", a: "Yes, via a deed of indemnity. We provide the template and handle the re-issue." },
    ],
    related: ["allot-shares", "transfer-shares", "shareholder-management"],
  },
  {
    slug: "shareholder-management",
    kind: "service",
    Icon: Users,
    eyebrow: "Shareholder management",
    title: "One register for every",
    highlight: "share movement.",
    subhead: "Members register, share classes, allotments, transfers, EMI options, accurate, audit-grade, ECCTA-aligned.",
    intro: "Whether you manage one company or fifty, your members register has to be accurate, ordered and exportable on demand. CoSec App keeps everything in one place, share classes, allotments, transfers, certificates, and EMI option pools, and surfaces PSC events automatically when ownership thresholds change.",
    image: IMG.legal,
    benefits: [
      { t: "Share classes & series", d: "Ordinary, preference, growth, EMI." },
      { t: "PSC re-analysis", d: "Triggered automatically on every event." },
      { t: "Audit-grade history", d: "Every change, who and when." },
      { t: "Reports for due diligence", d: "Export the members register in seconds." },
    ],
    includes: ["Members register", "Share class ledger", "Allotment & transfer history", "EMI option pool tracking", "PSC re-analysis"],
    howItWorks: [
      { t: "Migrate or onboard", d: "From spreadsheets, Diligent or Inform Direct." },
      { t: "Run share events", d: "Allotments, transfers, buybacks, in one flow." },
      { t: "Report & due diligence", d: "Members register exported in seconds." },
    ],
    pricing: { from: "Included", plan: "On every CoSec App plan" },
    faqs: [
      { q: "Do you handle EMI options and growth shares?", a: "Yes. Both are supported, including pool tracking, exercise events and HMRC reporting." },
      { q: "Can I export the members register?", a: "Yes. PDF, CSV and Excel exports are available for due diligence and audit purposes." },
    ],
    related: ["share-capital", "allot-shares", "transfer-shares"],
  },
  {
    slug: "change-company-name",
    kind: "service",
    Icon: Pencil,
    eyebrow: "Company name change",
    title: "Change your company name -",
    highlight: "in 24 hours.",
    subhead: "NM01 filing, board + shareholder resolutions, register updates and a new Certificate of Incorporation on Change of Name.",
    intro: "Changing a UK company name needs a special resolution (75% shareholder approval), an NM01 filing with Companies House, and a refresh of every statutory register that references the old name. We coordinate the lot.",
    image: IMG.doc,
    benefits: [
      { t: "Same-day NM01", d: "Filed via Companies House gateway." },
      { t: "Resolutions drafted", d: "Board + special shareholder resolution." },
      { t: "Registers updated", d: "Every statutory record refreshed." },
      { t: "New certificate", d: "Certificate of Incorporation on Change of Name." },
    ],
    includes: ["Board + special shareholder resolutions", "NM01 filing", "New Certificate of Incorporation on Change of Name", "Statutory register updates"],
    howItWorks: [
      { t: "Confirm the new name", d: "We check availability + restrictions." },
      { t: "Resolutions e-signed", d: "Inside CoSec App." },
      { t: "NM01 filed + certificate", d: "Typically within 24 hours." },
    ],
    pricing: { from: "£45", plan: "Per change" },
    faqs: [
      { q: "Is a shareholder resolution always needed?", a: "Yes, changing a company name requires a special resolution (75% of voting shareholders), unless the company's articles permit it by board resolution." },
      { q: "How fast is the name change?", a: "Usually within 24 hours of filing. Same-day name change is available via Companies House for an additional fee." },
    ],
    related: ["company-formation", "company-stamp-name-plate", "statutory-registers"],
  },
  {
    slug: "company-stamp-name-plate",
    kind: "service",
    Icon: Stamp,
    eyebrow: "Company stamps & name plates",
    title: "Premium company stamps",
    highlight: "and door plates.",
    subhead: "Self-inking company stamps, brass name plates and seal embossers. UK-delivered next day.",
    intro: "Every UK company benefits from a proper company stamp for share certificates, statutory documents and contracts. We supply self-inking pre-inked stamps, embossing seals and brass name plates, all UK-manufactured and next-day delivered.",
    image: IMG.studio,
    benefits: [
      { t: "Pre-inked self-inking", d: "Up to 10,000 impressions per pad." },
      { t: "Embossing seal upgrade", d: "Traditional company seal style." },
      { t: "Brass name plates", d: "Engraved for registered office signage." },
      { t: "Next-day UK delivery", d: "Tracked and signed for." },
    ],
    includes: ["Self-inking company stamp (round or square)", "Optional embossing seal", "Optional brass name plate (engraved)", "Tracked UK delivery"],
    howItWorks: [
      { t: "Choose your stamp", d: "Round, square, embossing or brass plate." },
      { t: "Send us the artwork", d: "Or use the auto-generated design." },
      { t: "Manufactured & shipped", d: "Next-day UK tracked delivery." },
    ],
    pricing: { from: "£28", plan: "Per item" },
    faqs: [
      { q: "Is a company seal legally required?", a: "No, the Companies Act 2006 made the use of a company seal optional. Most UK companies still use one for share certificates and formal contracts." },
      { q: "Can I have multiple impressions on one stamp?", a: "Yes, custom multi-line stamps with logos are supported." },
    ],
    related: ["company-formation", "change-company-name"],
  },
  {
    slug: "apostilled-documents-2",
    kind: "service",
    Icon: Globe2,
    eyebrow: "Apostilled documents",
    title: "Apostilled UK documents,",
    highlight: "for international use.",
    subhead: "Certified copies of UK statutory documents, apostilled by the FCDO, accepted in every Hague Convention country.",
    intro: "If you're using a UK Certificate of Incorporation, set of articles or director appointment overseas, the receiving country usually wants an apostille, a special FCDO stamp certifying that the UK signatures are genuine. We handle the certified copy, the notarisation, and the FCDO apostille, and courier the originals worldwide.",
    image: IMG.doc,
    benefits: [
      { t: "FCDO apostille", d: "Accepted in 124 Hague Convention countries." },
      { t: "Notarisation included", d: "Solicitor / notary certified copy." },
      { t: "Worldwide courier", d: "DHL Express to any country." },
      { t: "Statutory document range", d: "Certificate of Incorporation, articles, board minutes, more." },
    ],
    includes: ["Certified copy", "Notary / solicitor authentication", "FCDO apostille", "DHL Express worldwide courier"],
    howItWorks: [
      { t: "Choose the document", d: "Certificate of Incorporation, articles, minutes, more." },
      { t: "We notarise + apostille", d: "Via the FCDO Legalisation Office." },
      { t: "Worldwide delivery", d: "DHL Express to any country." },
    ],
    pricing: { from: "£140", plan: "Per document + courier" },
    faqs: [
      { q: "What's an apostille and why do I need it?", a: "An apostille is a special FCDO stamp under the 1961 Hague Convention. It certifies that a UK signature and seal are genuine, so a foreign country can accept the document without further authentication." },
      { q: "How long does it take?", a: "Standard apostille turnaround is 5–7 working days; an emergency 24-hour service is available for an additional fee." },
      { q: "Which documents can be apostilled?", a: "Any UK public document. Certificate of Incorporation, articles, board minutes, statutory declarations, powers of attorney, signed share certificates." },
    ],
    related: ["company-formation", "change-company-name"],
  },
  {
    slug: "company-secretarial-services",
    kind: "service",
    Icon: Briefcase,
    eyebrow: "Outsourced company secretarial",
    title: "Outsource your full",
    highlight: "Cosec function.",
    subhead: "Dedicated chartered company secretary, full statutory work, board support, without hiring in-house.",
    intro: "For UK SMEs, growth-stage companies and PE portfolios that don't want to hire a full-time company secretary. We embed a chartered Cosec into your team, run every statutory filing, prepare every board pack, maintain every register, all inside CoSec App, with your branding.",
    image: IMG.meeting,
    benefits: [
      { t: "Dedicated chartered secretary", d: "Named contact, on your account." },
      { t: "Full statutory coverage", d: "Every filing, register, board meeting." },
      { t: "Group structures", d: "Up to 50 entities included on the firm plan." },
      { t: "Inside CoSec App", d: "You have full visibility, signed audit trail." },
    ],
    includes: ["Dedicated chartered Cosec", "All Companies House filings", "All board + committee minutes", "All statutory registers maintained", "ECCTA compliance evidence", "Quarterly governance review"],
    howItWorks: [
      { t: "Onboard your entities", d: "Migrate from spreadsheets or other software." },
      { t: "Cosec embedded", d: "Named secretary on your account." },
      { t: "Run the function", d: "We file, draft, minute and audit, you have visibility." },
    ],
    pricing: { from: "£295 / mo", plan: "Per entity" },
    faqs: [
      { q: "Are your secretaries chartered?", a: "Yes, every dedicated secretary is a chartered ICSA / CGI member with 5+ years' UK statutory experience." },
      { q: "Can I keep my in-house team and use you for overflow?", a: "Absolutely, many firms use us for peak cycles (board season, year-end, PE portfolio onboarding) and keep their in-house team on day-to-day work." },
      { q: "What if I just want filings?", a: "See our individual services. Confirmation Statement, PSC, Appointments, Share Allotments, etc. The outsourced service bundles them with board support and a dedicated secretary." },
    ],
    related: ["confirmation-statement", "psc-services", "board-committee-meetings"],
  },
];

// ============================================================
//  FEATURES, modules inside CoSec App (deep dives)
// ============================================================
export const FEATURES = [
  {
    slug: "statutory-registers",
    kind: "feature",
    Icon: ScrollText,
    eyebrow: "Statutory registers",
    title: "Real-time statutory registers,",
    highlight: "audit-grade by default.",
    subhead: "Members, directors, secretaries, PSCs, charges and more, kept live, attributed, and exportable on demand.",
    intro: "Every UK company has to keep statutory registers. Most do it in version-controlled spreadsheets that fall apart at the first transfer or appointment. CoSec App keeps them live, attributes every change to a user, and writes a timestamped audit log so your register holds up in due diligence and in court.",
    image: IMG.legal,
    subFeatures: [
      { Icon: ScrollText, t: "Every UK statutory register", d: "Members, directors, secretaries, PSCs, charges, debenture holders." },
      { Icon: BadgeCheck, t: "Live attribution", d: "Every change is timestamped and attributed to a named user." },
      { Icon: FileText, t: "One-click export", d: "PDF, CSV and Excel exports for audit and due-diligence." },
      { Icon: ShieldCheck, t: "Pre-validated for Companies House", d: "Data integrity check before every filing." },
    ],
    related: ["e-fillings", "psc-services", "people-management"],
  },
  {
    slug: "e-fillings",
    kind: "feature",
    Icon: ArrowUpRightSquare,
    eyebrow: "Companies House e-filing",
    title: "Companies House filings,",
    highlight: "from one place.",
    subhead: "CS01, AP01, TM01, PSC, SH01, NM01, all filed via the Companies House software gateway, in one click.",
    intro: "We are an Authorised Software Provider for Companies House. That means filings go via the protocol gateway, no WebFiling logins, no manual form entry, no risk of typos.",
    image: IMG.doc,
    subFeatures: [
      { Icon: ArrowUpRightSquare, t: "Direct software gateway", d: "Filings submitted via Companies House protocol." },
      { Icon: BadgeCheck, t: "Authorised filer", d: "We are an Authorised Software Provider." },
      { Icon: Workflow, t: "Bulk filing", d: "Up to 500 entities per submission window." },
      { Icon: FileText, t: "Filing receipts archived", d: "Stored against the entity for audit." },
    ],
    related: ["statutory-registers", "psc-services", "automated-workflow"],
  },
  {
    slug: "people-management",
    kind: "feature",
    Icon: Users,
    eyebrow: "Directors, secretaries, officers",
    title: "Every officer, every appointment,",
    highlight: "fully tracked.",
    subhead: "Directors, secretaries, PSCs and authorised signatories, appointments, resignations, ECCTA verification, all in one place.",
    intro: "Track every person involved in every entity, directors, secretaries, PSCs and authorised signatories, with appointment dates, resignation dates, ECCTA identity verification status and signed consents, all in one place.",
    image: IMG.team,
    subFeatures: [
      { Icon: Users, t: "Officers across entities", d: "See where each person serves, directors, secretaries, PSCs." },
      { Icon: BadgeCheck, t: "ECCTA verification", d: "Identity status surfaced on every record." },
      { Icon: Calendar, t: "Appointment + resignation history", d: "Full audit trail per officer." },
      { Icon: FileSignature, t: "Signed consents", d: "Stored against the appointment." },
    ],
    related: ["statutory-registers", "psc-services", "role-based-authentication"],
  },
  {
    slug: "share-capital",
    kind: "feature",
    Icon: BarChart3,
    eyebrow: "Share capital",
    title: "Share capital,",
    highlight: "without the spreadsheets.",
    subhead: "Allotments, transfers, consolidations, sub-divisions, redenominations, every event captured, every register updated.",
    intro: "Share capital changes drive almost every statutory event in a UK company. CoSec App captures every allotment, transfer, buyback, consolidation, sub-division and redenomination, and updates the members register, PSC analysis and Companies House filings automatically.",
    image: IMG.studio,
    subFeatures: [
      { Icon: BarChart3, t: "All share classes", d: "Ordinary, preference, growth, EMI, alphabet." },
      { Icon: Repeat2, t: "Allotments + transfers", d: "Pre-populated SH01 and stock transfer forms." },
      { Icon: BadgeCheck, t: "PSC re-analysis", d: "Triggered automatically on every share event." },
      { Icon: FileText, t: "Members register updated", d: "Live, with full audit trail." },
    ],
    related: ["shareholder-management", "allot-shares", "transfer-shares"],
  },
  {
    slug: "board-committee-meetings",
    kind: "feature",
    Icon: Briefcase,
    eyebrow: "Board & committee meetings",
    title: "Board meetings, run end-to-end.",
    highlight: "Agenda to signed minutes.",
    subhead: "Schedule meetings, build board packs, distribute, capture minutes (AI-assisted), sign and archive, all in one workspace.",
    intro: "Board meetings drive governance. CoSec App lets you schedule the meeting, build the agenda and board pack, distribute securely, capture minutes with AI assistance, e-sign on the spot, and archive against the entity, all in one workspace.",
    image: IMG.board,
    subFeatures: [
      { Icon: Calendar, t: "Scheduling + invites", d: "Calendar invites + RSVP tracking." },
      { Icon: FileText, t: "Board pack builder", d: "Drag-drop documents into a single PDF." },
      { Icon: PenLine, t: "AI-assisted minutes", d: "Recordings turn into clean, structured drafts." },
      { Icon: FileSignature, t: "e-Signed resolutions", d: "Signed in-meeting via embedded e-signature." },
    ],
    related: ["automated-documentation", "e-signatures", "task-tracking"],
  },
  {
    slug: "automated-documentation",
    kind: "feature",
    Icon: FileSignature,
    eyebrow: "Automated documentation",
    title: "Documents that draft themselves",
    highlight: "from live data.",
    subhead: "Resolutions, minutes, share certificates, statutory letters, generated from CoSec App data with the right names, dates and numbers.",
    intro: "Most company secretarial documents follow a template. CoSec App ships those templates, pulls the live data, entity name, director, share class, PSC, and produces a polished document in seconds. No more copy-paste errors.",
    image: IMG.doc,
    subFeatures: [
      { Icon: FileSignature, t: "Dynamic templates", d: "Resolutions, minutes, NDAs, consents." },
      { Icon: FileText, t: "Live data merge", d: "Names, dates, share numbers, pulled automatically." },
      { Icon: PenLine, t: "Editable + brandable", d: "Override anything; firm-branded by default." },
      { Icon: BadgeCheck, t: "Versioned + archived", d: "Every revision saved against the entity." },
    ],
    related: ["board-committee-meetings", "e-signatures", "automated-workflow"],
  },
  {
    slug: "e-signatures",
    kind: "feature",
    Icon: PenLine,
    eyebrow: "e-Signatures",
    title: "Sign statutory documents",
    highlight: "in minutes, not weeks.",
    subhead: "Built-in e-signatures with full evidence trail, eIDAS compliant and audit-grade.",
    intro: "Embedded e-signatures with a full evidence trail. IP address, timestamp, geolocation, signer device fingerprint. eIDAS compliant, accepted by every UK court, and significantly cheaper than DocuSign-style external services.",
    image: IMG.laptop,
    subFeatures: [
      { Icon: PenLine, t: "Inline signing", d: "Sign documents inside CoSec App, no extra apps." },
      { Icon: BadgeCheck, t: "eIDAS compliant", d: "Audit-grade signature evidence." },
      { Icon: ShieldCheck, t: "Multi-signer flows", d: "Sequential, parallel, hybrid signing orders." },
      { Icon: FileText, t: "Archived to the entity", d: "Signed PDF + evidence stored automatically." },
    ],
    related: ["automated-documentation", "board-committee-meetings"],
  },
  {
    slug: "automated-workflow",
    kind: "feature",
    Icon: Workflow,
    eyebrow: "Workflow automation",
    title: "Compliance workflows",
    highlight: "that run themselves.",
    subhead: "Build repeatable workflows, incorporations, board cycles, PSC reviews, year-end, and run them across hundreds of entities with one click.",
    intro: "Most Cosec work is repeatable. CoSec App lets you build a workflow once, incorporation, monthly board cycle, year-end review, and run it across hundreds of entities with one click. Fewer steps, fewer mistakes, much faster cycles.",
    image: IMG.laptop,
    subFeatures: [
      { Icon: Workflow, t: "Build once, run anywhere", d: "Workflow templates apply across entities." },
      { Icon: Calendar, t: "Scheduled triggers", d: "Date-based, event-based, recurring." },
      { Icon: Bell, t: "Auto-reminders", d: "Email, in-app and dashboard alerts." },
      { Icon: BadgeCheck, t: "Audit trail per run", d: "Every step recorded, signed and timestamped." },
    ],
    related: ["task-tracking", "alerts-reminders", "automated-documentation"],
  },
  {
    slug: "task-tracking",
    kind: "feature",
    Icon: Calendar,
    eyebrow: "Task tracking",
    title: "Every compliance task,",
    highlight: "owned and tracked.",
    subhead: "Plan tasks, assign owners, set deadlines and follow-up cadences, and watch every responsibility close on time.",
    intro: "Statutory deadlines aren't optional. CoSec App tracks every compliance task, confirmation statements, PSC updates, board meetings, annual returns, assigns an owner, sets a cadence and chases until done.",
    image: IMG.desk,
    subFeatures: [
      { Icon: Calendar, t: "Task assignment", d: "Owner, due date, escalation path." },
      { Icon: Bell, t: "Smart reminders", d: "Configurable cadence per task." },
      { Icon: BadgeCheck, t: "Status dashboard", d: "Pending, in-progress, done, at a glance." },
      { Icon: FileText, t: "Audit trail", d: "Every status change tracked." },
    ],
    related: ["alerts-reminders", "automated-workflow"],
  },
  {
    slug: "alerts-reminders",
    kind: "feature",
    Icon: Bell,
    eyebrow: "Alerts & reminders",
    title: "Statutory deadlines that",
    highlight: "never sneak up on you.",
    subhead: "Configurable email, in-app and dashboard alerts so every CS01, PSC update and board cycle is on the radar.",
    intro: "Set a 60/30/7 day reminder cadence on every statutory deadline. Configure escalation to the company secretary, then the FD, then the board. Never explain a missed CS01 again.",
    image: IMG.desk,
    subFeatures: [
      { Icon: Bell, t: "Multi-channel alerts", d: "Email, in-app, dashboard, mobile push." },
      { Icon: Calendar, t: "Configurable cadence", d: "60/30/7 days, or your own schedule." },
      { Icon: Users, t: "Escalation rules", d: "Secretary → FD → board, automatic." },
      { Icon: BadgeCheck, t: "Snooze + acknowledge", d: "Tracked, attributed, archived." },
    ],
    related: ["task-tracking", "automated-workflow"],
  },
  {
    slug: "role-based-authentication",
    kind: "feature",
    Icon: KeyRound,
    eyebrow: "Role-based access",
    title: "Role-based access,",
    highlight: "matter-level granularity.",
    subhead: "Give the right people access to the right entities, without showing the wrong matter to the wrong team.",
    intro: "Firms work across many entities, but matter teams should only see what they're authorised to see. CoSec App gives you fine-grained role-based access, at firm, team, matter and entity level, with full audit logs of every access event.",
    image: IMG.laptop,
    subFeatures: [
      { Icon: KeyRound, t: "Fine-grained roles", d: "Firm, team, matter, entity level." },
      { Icon: ShieldCheck, t: "SSO + MFA", d: "Okta, Azure AD, Google Workspace; TOTP/WebAuthn." },
      { Icon: FileText, t: "Access audit log", d: "Every login and access recorded." },
      { Icon: Users, t: "Joiner / leaver workflow", d: "Provision and de-provision in one place." },
    ],
    related: ["multi-device-support", "people-management"],
  },
  {
    slug: "multi-device-support",
    kind: "feature",
    Icon: MonitorSmartphone,
    eyebrow: "Multi-device access",
    title: "Same data,",
    highlight: "every device.",
    subhead: "Desktop, tablet, mobile, every CoSec App feature works anywhere your team works.",
    intro: "CoSec App is built for modern teams, web-first, fully responsive, with native-quality experiences on tablet and mobile. Approve a filing from your phone; review a board pack on your tablet; run bulk CS01 from your desktop.",
    image: IMG.mobile,
    subFeatures: [
      { Icon: MonitorSmartphone, t: "Web + tablet + mobile", d: "Every feature, every device." },
      { Icon: ShieldCheck, t: "Encrypted offline cache", d: "Read board packs offline; sync when you reconnect." },
      { Icon: Bell, t: "Push notifications", d: "Approval requests + alerts on the go." },
      { Icon: BadgeCheck, t: "Same audit trail", d: "Mobile actions logged exactly like desktop." },
    ],
    related: ["role-based-authentication", "alerts-reminders"],
  },
  {
    slug: "organisation-structure",
    kind: "feature",
    Icon: GitBranch,
    eyebrow: "Group structure",
    title: "Group structures,",
    highlight: "visualised.",
    subhead: "Parent–subsidiary trees, cross-holdings, inter-company resolutions, see and manage the whole group at a glance.",
    intro: "If you run a group, an organisation chart isn't a nice-to-have, it's the only way to see beneficial ownership, intra-group resolutions, control chains and PSC paths. CoSec App builds one automatically from your members register, and updates it every time a share event happens.",
    image: IMG.legal,
    subFeatures: [
      { Icon: GitBranch, t: "Auto-built from data", d: "Group tree generated from members register." },
      { Icon: Network, t: "Cross-holdings & control", d: "Even complex Topco / Bidco / Holdco structures." },
      { Icon: FileText, t: "Beneficial ownership chains", d: "PSC paths surfaced through the structure." },
      { Icon: ShieldCheck, t: "Export for DD", d: "PDF + CSV for due diligence rooms." },
    ],
    related: ["statutory-registers", "shareholder-management", "psc-services"],
  },
];

// Helper: look up a slug → entry (services + features)
export function findCatalogEntry(slug) {
  const all = [...SERVICES, ...FEATURES];
  return all.find((e) => e.slug === slug) || null;
}
